import { getOffset, type TStatusManualStorage } from "@package/grid-utils";
import { monthCodeFromDate } from "@package/grid-utils/month-code";
import { StatsTracker } from "@package/util/stats";
import { type Remote, wrap } from "comlink";
import { logger } from "@artis/logger";

import { CalcChannels, type PortLike, PortAdapter, SwappablePort } from "../transport";
import { createReplicatedGrid, type ReplicatedGrid } from "../lib/grid";
import type { InboundEvent, OutboundEvent } from "../lib/runtime";
import type { WorkerApi } from "../lib/worker";
import { PendingCellEdits } from "./edits";
import { HealthChecker } from "./health";
import { LivePriceTracer } from "./trace";
import { utcNow } from "../../utils";

export class CalcClient {
  /** Previously this was 5 seconds however we have increased it to 10 seconds to account for slower computers */
  static readonly WORKER_RESPONSE_TIMEOUT = 10_000;

  // ---------------- Transport ----------------
  /** The active worker port */
  private _port = new SwappablePort();
  /** Comlink remote proxy to the worker api */
  private _api: Remote<WorkerApi>;
  /** Subscriptions to outbound events */
  private _subscriptions: Set<(event: OutboundEvent) => void> = new Set();

  // ---------------- Features ----------------
  /** Grid values cache that stays in sync with the worker */
  public grid: ReplicatedGrid;

  /** Main-thread stages of the worker's sampled live-price traces */
  public trace = new LivePriceTracer();

  /** Batching and optimistic state for cell edits */
  public edits = new PendingCellEdits(
    async (...edit) => this.api.cellEdit(...edit),
    (...args) => this.api.getGrid(...args),
  );

  /** Replay messages to the worker when it is restarted */
  private replayer = new MessageReplayer((message) => this.api.send(message));

  /** Callbacks fired after initial start() and after every rebuild + replay. */
  private _readyCbs = new Set<() => void | Promise<void>>();
  private notifyReady = async () => {
    for (const cb of this._readyCbs) {
      try {
        await cb();
      } catch (e) {
        logger.warn("CalcClient onReady callback failed", { error: String(e) });
      }
    }
  };

  /** Restart the worker when it becomes unhealthy */
  private health = new HealthChecker({
    taskTimeout: CalcClient.WORKER_RESPONSE_TIMEOUT,
    health: () => this.api.getState(),
    rebuild: () => Promise.resolve(this.reconnect()),
    restart: async () => {
      await new Promise((resolve) => setTimeout(resolve, 5_000));
      await this.api.start();
      await this.replayer.replay();
      await this.notifyReady();
    },
  });

  /** Performance tracking for calls to the worker */
  public stats = new StatsTracker<string>(5 * 60 * 1000); // 5 minutes window

  /** Channels for communicating with the worker */
  public channels: CalcChannels;

  constructor(private readonly createWorker: () => Worker | SharedWorker | PortAdapter) {
    const channels = new CalcChannels(PortAdapter.from(this._port));
    this.channels = channels;
    this.grid = createReplicatedGrid({ channel: channels.channel("grid/replicated"), name: "main" });
    this._api = this.wrapPort(this._port);
    this.connect();
    this.grid.requestSnapshot();
    channels.on("log/event", (event) => logger.event(event));
    channels.on("log/span-enter", (event) => logger.enter(event));
    channels.on("log/span-exit", (event) => logger.exit(event));
    channels.on("calc/outbound", (event) => {
      if (event.type === "live-prices-trace") this.trace.receive(event.data);
      this._subscriptions.forEach((cb) => cb(event));
    });
    this.grid.onApplied((_, version) => this.trace.applied(version));
  }

  get api() {
    return this._api;
  }

  private _everStarted = false;
  start = () =>
    this.syncTask(async () => {
      this.health.start();
      await this.api.start();
      // resumes (online/pause toggles) re-invoke start() without a rebuild; real rebuilds fire onReady via health.restart()
      if (this._everStarted) return;
      this._everStarted = true;
      await this.notifyReady();
    });

  onReady = (cb: () => void | Promise<void>) => {
    this._readyCbs.add(cb);
    return () => {
      this._readyCbs.delete(cb);
    };
  };

  stop = () =>
    this.syncTask(() => {
      this.health.stop();
      return this.api.stop();
    });

  send = (message: InboundEvent) => this.replayer.send(message);

  cellEdit = (...edit: TCellEdit[]) =>
    this.edits.edit(
      ...edit.map((x) => {
        const rowId = x.rowId ?? monthCodeFromDate(new Date(x.month));
        const offset = x.offset ?? getOffset(x.month)!;
        return { ...x, edited_at: utcNow().valueOf(), rowId, offset, field: "value" };
      }),
    );

  getGrid = (products: (readonly [product: string, field: string])[], rows: (string | number)[]) => {
    return products.map(([product, field]) => rows.map((row) => this.grid.readCell(product, field, row)));
  };

  getCell = (product: string, field: string, row: string | number) =>
    this.edits.cellWithPending(this.grid.readCell(product, field, row), product, field, row);

  getGridValues = (
    products: (readonly [product: string, field: string])[],
    rows: (string | number)[],
  ): (number | null)[][] => {
    return this.getGrid(products, rows).map((row) => row.map((cell) => cell.Ok ?? null));
  };

  /** Add parent api hooks for the worker  */
  onEvent: (cb: (event: OutboundEvent) => void) => () => void = (cb) => {
    this._subscriptions.add(cb);
    return () => this._subscriptions.delete(cb);
  };

  private reconnect = () => {
    if (this._port) this._port.close();
    this.trace.reset();
    this.connect();
    this.grid.requestSnapshot();
  };

  private connect = () => {
    const transport = PortAdapter.from(this.createWorker());
    this._port.replacePort(transport.port);
    // biome-ignore lint/suspicious/noExplicitAny: Usefull hook to kill the worker for debugging
    if (typeof window !== "undefined") (window as any)._kill_worker = () => transport.close?.();
  };

  private wrapPort = (endpoint: PortLike) => {
    const api = wrap<WorkerApi>(endpoint);
    const task = <R>(_name: string, fn: () => Promise<R>) => this.task(fn);
    const stats = this.stats;
    const proxy = new Proxy(api, {
      get(_, prop) {
        const name = prop as string;
        // biome-ignore lint/suspicious/noExplicitAny: no type
        return (...args: any[]) => {
          return stats.track(name, () =>
            task(name, () => {
              // biome-ignore lint/suspicious/noExplicitAny: no type
              return (api as any)[name](...args);
            }),
          );
        };
      },
    });
    return proxy;
  };

  /** Wraps a task with a timeout and error handling */
  private task = <R>(fn: () => Promise<R>) => this.health.task(fn);

  /** Wraps a task in a promise that is resolved when the previous task is complete */
  private _task = Promise.resolve();
  private syncTask = (f: () => Promise<void> | void) => {
    this._task = this._task.then(() => Promise.resolve(f()));
    return this._task;
  };
}

/**
 * If the worker is restarted, we need to replay latest message of each type from the main thread to the worker.
 *
 * Note: We skip cell-edits which are ephemeral and should not be replayed.
 **/
class MessageReplayer {
  latest: { [K in InboundEvent["type"]]?: InboundEvent["data"] } = {};

  constructor(private readonly sender: (message: InboundEvent) => Promise<void>) {}

  send = (message: InboundEvent) => {
    if (message.type !== "cell-edits") this.latest[message.type] = message.data;
    this.sender(message).catch(() => {});
  };

  replay = async () => {
    for (const [type, data] of Object.entries(this.latest)) {
      if (!data) continue;
      await this.sender({ type, data } as InboundEvent);
    }
  };
}

export type TCellEdit = {
  product: string;
  result: number | null;
  month: string;
  storageType: TStatusManualStorage | "broadcast";
  offset?: number;
  field?: string;
  rowId?: string;
  force?: boolean;
  edited_at?: number;
};
